import { IWithContextProps, consumer, withContext } from '@tock/coretypes';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import AccountActivateRoute from 'routes/protected/account/AccountActivateRoute';
import AccountRoute from 'routes/protected/account/AccountRoute';
import BaseComponent from '@tock/shared/components/BaseComponent';
import EmailChangeCancelRoute from 'routes/unprotected/EmailChangeCancelRoute';
import EmailChangeRoute from 'routes/unprotected/EmailChangeRoute';
import FlowRoute, { FlowRouteComponentProps } from 'components/render-prop-components/FlowRoute';
import ForgotPasswordRoute from 'routes/unprotected/ForgotPasswordRoute';
import LogoutRoute from 'routes/protected/LogoutRoute';
import PartnerBookingsRoute from 'routes/protected/account/PartnerBookingsRoute';
import ProfileMetadataRoute from 'routes/account/ProfileMetadataRoute';
import ProfileReceiptRoute from 'routes/business/ProfileReceiptRoute';
import ProfileRoute from 'routes/protected/account/profile/ProfileRoute';
import ProtectedRoute from 'routes/ProtectedRoute';
import RescheduleFlowRoute, {
  reschedulePageOrder,
} from 'routes/protected/account/reschedule/RescheduleFlowRoute';
import ResetPasswordRoute from 'routes/unprotected/ResetPasswordRoute';
import SignedOutRoute, {
  SignedOutRouteProps,
  profileSignedOutProps,
  reservationSignedOutProps,
} from 'routes/unprotected/SignedOutRoute';
import SigninRoute from 'routes/unprotected/SigninRoute';
import SyndicatedRoute from 'routes/business/syndicate-flow/SyndicatedRoute';
import TransferInitiationFlowRoute from 'routes/protected/account/transfer/TransferInitiationFlowRoute';
import VerifyTransferRoute from 'routes/protected/account/transfer/VerifyTransferRoute';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

interface ConnectProps extends DefaultConnectProps {
  config?: consumer.ConsumerConfig;
  lock: StoreType['lock'];
  reschedule: StoreType['reschedule'];
}
type Props = ConnectProps & RouteComponentProps & IWithContextProps;

class ProfileHomeRoute extends BaseComponent<Props> {
  renderRedirect() {
    const { match } = this.props;
    return (
      <Redirect
        to={{
          ...this.props.location,
          pathname: `${match.path}/account`,
        }}
      />
    );
  }

  renderSignedOutRoute({ ...props }: SignedOutRouteProps) {
    return ({ ...routeProps }: RouteComponentProps) => (
      <SignedOutRoute {...props} {...routeProps} />
    );
  }

  renderRescheduleFlowRoute(
    routeProps: RouteComponentProps,
    flowRouteProps: FlowRouteComponentProps
  ) {
    return <RescheduleFlowRoute {...routeProps} {...flowRouteProps} />;
  }

  render() {
    const { app, match } = this.props;
    const matchUrl = match.url;

    return (
      <>
        <ProfileMetadataRoute />
        <Switch>
          <Route path={`${match.path}/activate`} component={AccountActivateRoute} />
          {/* Unprotected Routes */}
          <Route
            path={`${match.path}/forgot-password`}
            render={(routeProps: RouteComponentProps) => (
              <ForgotPasswordRoute {...routeProps} baseRoute={matchUrl} />
            )}
          />
          <Route path={`${match.path}/:loginOrSignup(login|signup)`} component={SigninRoute} />
          {/* The following /signed-out routes can be deprecated when the new guest navigation is rolled out to
              all guests, as the signed-out pages are being deprecated - Brian P. 7/29/21 */}
          <Route
            path={`${match.path}/account/signed-out`}
            render={this.renderSignedOutRoute(profileSignedOutProps)}
          />
          <Route
            path={`${match.path}/reservations/signed-out`}
            render={this.renderSignedOutRoute(reservationSignedOutProps)}
          />
          <Route
            path={`${match.path}/signed-out`}
            render={this.renderSignedOutRoute(reservationSignedOutProps)}
          />
          <Route
            path={`${match.path}/reservations/:purchaseId/signed-out`}
            render={this.renderSignedOutRoute(reservationSignedOutProps)}
          />
          <Route path={`${match.path}/reset`} component={ResetPasswordRoute} />
          {/* TODO delete this once we confirm all traffic is going through :businessDomain prefixed route instead */}
          <FlowRoute<Props>
            {...this.props}
            isInitialized
            path={`${match.path}/reschedule`}
            pageOrder={reschedulePageOrder}
            render={this.renderRescheduleFlowRoute}
          />
          {/* Protected Routes */}
          <ProtectedRoute app={app} path={`${match.path}/account`} component={AccountRoute} />
          <ProtectedRoute app={app} path={`${match.path}/logout`} component={LogoutRoute} />
          <ProtectedRoute
            app={app}
            path={`${match.path}/email/change/cancel`}
            component={EmailChangeCancelRoute}
          />
          <ProtectedRoute
            app={app}
            path={`${match.path}/email/change`}
            component={EmailChangeRoute}
          />
          <ProtectedRoute
            app={app}
            path={`${match.path}/transfer`}
            component={VerifyTransferRoute}
          />
          <ProtectedRoute
            app={app}
            path={`${match.url}/start-transfer`}
            component={TransferInitiationFlowRoute}
          />
          <ProtectedRoute
            key="PartnerBookingsRoute"
            app={app}
            path={`${match.path}/reservations/partner`}
            exact
            component={PartnerBookingsRoute}
          />
          <ProtectedRoute
            key="SyndicatedRoute"
            app={app}
            path={`${match.path}/reservations/:purchaseId/syndicate`}
            exact
            component={SyndicatedRoute}
          />
          <ProtectedRoute
            key="ProfileReceiptRoute"
            app={app}
            path={`${match.path}/reservations/:purchaseId(\\d+)`}
            component={ProfileReceiptRoute}
          />
          <ProtectedRoute key="ProfileRoute" app={app} path={match.path} component={ProfileRoute} />

          <Route render={this.renderRedirect} />
        </Switch>
      </>
    );
  }
}

function mapStateToProps(state: StoreType) {
  return {
    config: state.business.config,
    lock: state.lock,
    reschedule: state.reschedule,
  };
}

export default connect<RouteComponentProps, ConnectProps>(
  withContext(ProfileHomeRoute),
  mapStateToProps
);
